<script setup lang="ts">
  defineOptions({ name: 'EventDetailExhibitorsSection' })

  import { computed, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { FilterLinesIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPButton from '@ui/components/CPButton.vue'
  import CPCheckbox from '@ui/components/CPCheckbox.vue'
  import CPPopover from '@ui/components/CPPopover.vue'
  import CPTag from '@ui/components/CPTag.vue'
  import CPText from '@ui/components/CPText.vue'

  import EventDetailExhibitorDrawer from './EventDetailExhibitorDrawer.vue'

  import EventDetailTabPanel from './EventDetailTabPanel.vue'

  import { useDirectoryPagination } from '../composables/useDirectoryPagination'

  import type { EventDetailExhibitor, EventDetailExhibitorAccentTone, EventDetailTagColor } from '../types/event-detail'

  import type { EventsExhibitorDetailResource } from '../types/events-detail-experience-controller'

  const props = defineProps({
    exhibitors: {
      type: Array as PropType<readonly EventDetailExhibitor[]>,
      required: true,
    },
    exhibitorDetail: {
      type: Object as PropType<EventsExhibitorDetailResource>,
      default: () => ({
        state: 'empty' as const,
        representatives: [],
      }),
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    openExhibitor: [exhibitorId: string]
    closeExhibitor: []
  }>()

  const ACCENT_TONE_CLASSES: Record<EventDetailExhibitorAccentTone, string> = {
    primary: 'bg-[var(--cp-primary-50)]',
    success: 'bg-[var(--cp-success-50)]',
    warning: 'bg-[var(--cp-warning-50)]',
    purple: 'bg-[var(--cp-secondary-50)]',
    error: 'bg-[var(--cp-error-50)]',
    gray: 'bg-[var(--cp-surface-100)]',
    pink: 'bg-[var(--cp-error-50)]',
  }

  const ACCENT_TONE_FALLBACK: readonly EventDetailExhibitorAccentTone[] = [
    'primary',
    'success',
    'warning',
    'purple',
    'error',
    'gray',
    'pink',
  ]

  const TIER_COLORS: Record<string, EventDetailTagColor> = {
    platinum: 'gray',
    diamond: 'blue-light',
    gold: 'warning',
    silver: 'gray',
    bronze: 'purple',
  }

  const PAGE_SIZE = 12

  const { t } = useI18n()

  const searchQuery = ref('')
  const selectedTiers = ref<string[]>([])

  const selectedExhibitor = ref<EventDetailExhibitor | null>(null)

  const uniqueTiers = computed(() => {
    const seen = new Set<string>()
    const tiers: string[] = []
    for (const exhibitor of props.exhibitors) {
      const tier = exhibitor.tag?.trim()
      if (!tier || seen.has(tier)) continue
      seen.add(tier)
      tiers.push(tier)
    }
    return tiers
  })

  const hasTierFilters = computed(() => uniqueTiers.value.length > 0)

  const matchedExhibitors = computed(() => {
    const allowed = new Set(uniqueTiers.value)
    const tiers = new Set(selectedTiers.value.filter(tier => allowed.has(tier)))
    const byTier =
      tiers.size === 0 ? [...props.exhibitors] : props.exhibitors.filter(exhibitor => Boolean(exhibitor.tag && tiers.has(exhibitor.tag)))
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return byTier
    return byTier.filter(
      exhibitor =>
        exhibitor.name.toLowerCase().includes(query) ||
        exhibitor.booth.toLowerCase().includes(query) ||
        (exhibitor.tag ?? '').toLowerCase().includes(query)
    )
  })

  const { pageItems, total, rangeStart, rangeEnd, canGoPrevious, canGoNext, goPrevious, goNext } = useDirectoryPagination(
    matchedExhibitors,
    PAGE_SIZE
  )

  const footerLabel = computed(() =>
    t('clientportal.events.detail.pageRange', {
      start: rangeStart.value,
      end: rangeEnd.value,
      total: total.value,
    })
  )

  const isDrawerOpen = computed(() => selectedExhibitor.value != null)

  const logoToneClass = (exhibitor: EventDetailExhibitor, index: number) => {
    const tone = exhibitor.accentTone ?? ACCENT_TONE_FALLBACK[index % ACCENT_TONE_FALLBACK.length]
    return ACCENT_TONE_CLASSES[tone]
  }

  const tagColor = (tag?: string): EventDetailTagColor => {
    if (!tag) return 'gray'
    const normalized = tag.trim().toLowerCase()
    return TIER_COLORS[normalized] ?? 'gray'
  }

  const openExhibitor = (exhibitor: EventDetailExhibitor) => {
    selectedExhibitor.value = exhibitor
    emit('openExhibitor', exhibitor.id)
  }

  const onDrawerShowChange = (show: boolean) => {
    if (show) return
    selectedExhibitor.value = null
    emit('closeExhibitor')
  }

  const isTierSelected = (tier: string) => selectedTiers.value.includes(tier)

  const setTierSelected = (tier: string, checked: boolean) => {
    if (checked) {
      if (!selectedTiers.value.includes(tier)) selectedTiers.value = [...selectedTiers.value, tier]
      return
    }
    selectedTiers.value = selectedTiers.value.filter(item => item !== tier)
  }

  const clearTierFilters = () => {
    selectedTiers.value = []
  }

  const tierFilterId = (tier: string) =>
    createElementId(
      props.idPrefix,
      'exhibitors-filter',
      tier
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'tier'
    )
</script>

<template>
  <div :id="createElementId(idPrefix, 'exhibitors', 'root')" class="mx-auto w-full max-w-[82.5rem] px-4 py-6 md:px-6">
    <EventDetailTabPanel
      :id-prefix="`${idPrefix}-exhibitors`"
      :title="t('clientportal.events.detail.exhibitorsTitle')"
      :search-placeholder="t('clientportal.events.detail.exhibitorsSearch')"
      :search-query="searchQuery"
      :footer-label="footerLabel"
      :can-go-previous="canGoPrevious"
      :can-go-next="canGoNext"
      :previous-label="t('clientportal.events.detail.previous')"
      :next-label="t('clientportal.events.detail.next')"
      @update:search-query="searchQuery = $event"
      @previous="goPrevious"
      @next="goNext"
    >
      <template #after-search>
        <CPPopover
          v-if="hasTierFilters"
          :id="createElementId(idPrefix, 'exhibitors', 'tag-filters')"
          trigger="click"
          placement="bottom-end"
          :show-arrow="false"
        >
          <template #trigger>
            <CPButton :id="createElementId(idPrefix, 'exhibitors', 'filters-trigger')" type="secondary" color="secondary" size="sm">
              <template #iconLeft>
                <FilterLinesIcon class="h-4 w-4" aria-hidden="true" />
              </template>
              {{ t('clientportal.events.detail.exhibitorsFilters') }}
            </CPButton>
          </template>
          <div class="flex min-w-[14rem] flex-col gap-2 p-3">
            <div class="flex items-center justify-between gap-2">
              <CPText size="sm" weight="semibold" class="text-body">
                {{ t('clientportal.events.detail.exhibitorsFilterByTier') }}
              </CPText>
              <CPButton
                :id="createElementId(idPrefix, 'exhibitors', 'filters-clear')"
                type="text"
                color="secondary"
                size="xs"
                @click="clearTierFilters"
              >
                {{ t('clientportal.events.detail.exhibitorsFilterClear') }}
              </CPButton>
            </div>
            <div :id="createElementId(idPrefix, 'exhibitors', 'filters-options')" class="flex max-h-60 flex-col gap-2 overflow-y-auto">
              <CPCheckbox
                v-for="tier in uniqueTiers"
                :id="tierFilterId(tier)"
                :key="tier"
                :label="tier"
                :checked="isTierSelected(tier)"
                size="sm"
                @update:checked="checked => setTierSelected(tier, checked)"
              />
            </div>
          </div>
        </CPPopover>
      </template>
      <div class="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 md:p-6 lg:grid-cols-3">
        <div
          v-for="(exhibitor, index) in pageItems"
          :id="createElementId(idPrefix, 'exhibitor', exhibitor.id)"
          :key="exhibitor.id"
          role="button"
          tabindex="0"
          class="flex cursor-pointer items-start gap-3 rounded-[10px] border border-solid border-[var(--cp-border-500)] p-4 hover:bg-[var(--cp-hover)]"
          :aria-label="t('clientportal.events.detail.exhibitorsOpenDetails', { name: exhibitor.name })"
          @click="openExhibitor(exhibitor)"
          @keydown.enter.prevent="openExhibitor(exhibitor)"
          @keydown.space.prevent="openExhibitor(exhibitor)"
        >
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] text-base font-bold text-700"
            :class="logoToneClass(exhibitor, index)"
            aria-hidden="true"
          >
            {{ exhibitor.initial }}
          </div>
          <div class="min-w-0 flex-1">
            <CPText size="lg" weight="semibold" html-tag="div" class="truncate text-body">
              {{ exhibitor.name }}
            </CPText>
            <CPText v-if="exhibitor.booth" size="sm" weight="normal" html-tag="div" class="mt-0.5 truncate text-500">
              {{ exhibitor.booth }}
            </CPText>
            <div v-if="exhibitor.tag" class="mt-2.5">
              <CPTag
                :id="createElementId(idPrefix, 'exhibitor-tag', exhibitor.id)"
                size="xs"
                :color="tagColor(exhibitor.tag)"
                :bordered="false"
                :interactive="false"
              >
                <CPText size="xs" weight="bold">{{ exhibitor.tag }}</CPText>
              </CPTag>
            </div>
          </div>
        </div>
      </div>

      <CPText v-if="pageItems.length === 0" size="md" weight="normal" class="px-4 pb-8 text-center text-500 md:px-6">
        {{ t('clientportal.events.detail.exhibitorsEmpty') }}
      </CPText>
    </EventDetailTabPanel>

    <EventDetailExhibitorDrawer
      :show="isDrawerOpen"
      :exhibitor="selectedExhibitor"
      :state="exhibitorDetail.state"
      :representatives="exhibitorDetail.representatives"
      :id-prefix="idPrefix"
      @update:show="onDrawerShowChange"
    />
  </div>
</template>
